import React from 'react'
import _ from 'underscore'
import { Check, ChevronDown, ChevronUp, ExternalLink, LoaderCircle, Plus, Search, Tags, Users, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Card,
  CardAction,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { InputGroup, InputGroupAddon, InputGroupInput } from '@/components/ui/input-group'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { cn } from '@/lib/utils'
import PillSelect from '../../../../pill_select'
import TutorResultsPartial from './partial'
import store from '../../../../../state/store'
import SearchTutors from '../add/show'

const COLUMN_HEADING_CLASSES = 'whitespace-nowrap text-center text-xs font-semibold uppercase tracking-wide text-muted-foreground'

const canNotify = (tutor_result) => {
  const job_candidate = tutor_result.job_candidate

  return !tutor_result.tutor.ghosted &&
    !job_candidate.notified &&
    !job_candidate.interested &&
    !job_candidate.applied &&
    !job_candidate.proposed &&
    !job_candidate.accepted &&
    !job_candidate.declined &&
    !job_candidate.rejected
}

const canRenotify = (tutor_result) => {
  const job_candidate = tutor_result.job_candidate

  return !tutor_result.tutor.ghosted &&
    job_candidate.notified &&
    !job_candidate.interested &&
    !job_candidate.applied &&
    !job_candidate.proposed &&
    !job_candidate.accepted &&
    !job_candidate.declined &&
    !job_candidate.rejected
}

const canReject = (tutor_result) => {
  const job_candidate = tutor_result.job_candidate

  return !job_candidate.rejected &&
    !job_candidate.accepted &&
    (job_candidate.notified ||
      job_candidate.interested ||
      job_candidate.applied ||
      job_candidate.proposed ||
      job_candidate.declined)
}

const isSelectable = (tutor_result) => canNotify(tutor_result) || canRenotify(tutor_result) || canReject(tutor_result)

class TutorResults extends React.Component {

  constructor(props) {
    super(props)

    this.state = {
      adding: false,
      tutor_search_term: '',
      search_bio_input: props.ui.tutoring_job.tutor_search_bio || '',
      selected_tutor_ids: []
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.tutoring_job?.id !== this.props.tutoring_job?.id) {
      this.clearSearchBio()
    }

    if (prevProps.tutor_results !== this.props.tutor_results && this.state.selected_tutor_ids.length > 0) {
      const valid_ids = _.filter(this.state.selected_tutor_ids, (tutor_id) => {
        return _.any(this.props.tutor_results, (tutor_result) => tutor_result.tutor.id === tutor_id)
      })

      if (valid_ids.length !== this.state.selected_tutor_ids.length) {
        this.setState({ selected_tutor_ids: valid_ids })
      }
    }
  }

  componentWillUnmount() {
    if (store.get().ui.tutoring_job.tutor_search_bio) {
      store.emit('ui:tutoring_job:tutor_search_bio', '')
    }
  }

  clearSearchBio = () => {
    if (store.get().ui.tutoring_job.tutor_search_bio) {
      store.emit('ui:tutoring_job:tutor_search_bio', '')
    }
    if (this.state.search_bio_input) {
      this.setState({ search_bio_input: '' })
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    if (nextProps.tutoring_job !== this.props.tutoring_job)
      return true

    if (nextProps.tutor_results !== this.props.tutor_results)
      return true

    if (nextProps.tutor_proposals !== this.props.tutor_proposals)
      return true

    if (nextProps.ui !== this.props.ui)
      return true

    if (nextState !== this.state)
      return true

    return false
  }

  onFilter = (value) => {
    store.emit('ui:tutoring_job:tutor_results_filter', value)

    store.emit('tutoring_job:tutor_results:index', this.props.tutoring_job.id,
      { filter: value }
    )
  }

  onExperienceLevel = (value) => {
    store.emit('ui:tutoring_job:tutor_experience_level', value)

    store.emit('tutoring_job:tutor_results:index', this.props.tutoring_job.id)
  }

  onActiveWithin = (value) => {
    store.emit('ui:tutoring_job:tutor_active_within', value)

    store.emit('tutoring_job:tutor_results:index', this.props.tutoring_job.id)
  }

  onOnboardedAfterFilter = (value) => {
    store.emit('ui:tutoring_job:onboarded_after', value)

    store.emit('tutoring_job:tutor_results:index', this.props.tutoring_job.id)
  }

  onSearchBioChange = (e) => {
    this.setState({ search_bio_input: e.target.value })
  }

  onSearchBioKeyPress = (e) => {
    if (e.key === 'Enter') {
      this.commitSearchBio(this.state.search_bio_input)
    }
  }

  onSearchBioClear = () => {
    this.setState({ search_bio_input: '' })
    this.commitSearchBio('')
  }

  commitSearchBio = (value) => {
    store.emit('ui:tutoring_job:tutor_search_bio', value)
    store.emit('tutoring_job:tutor_results:index', this.props.tutoring_job.id)
  }

  onSearchBioSuggestion = (value) => {
    this.setState({ search_bio_input: value })
    this.commitSearchBio(value)
  }


  onAdd = () => {
    this.setState({ adding: !this.state.adding })
  }

  onSearchTutorsChange = (value) => {
    this.setState({ tutor_search_term: value })
  }

  onAddTutor = (tutor) => {
    store.emit('tutoring_job:tutor_results:create', {
      tutor_id: tutor.id,
      tutoring_job_id: this.props.tutoring_job.id,
      applied: true
    })
  }

  selectableTutorIds = () => {
    return _.map(_.filter(this.props.tutor_results, isSelectable), (tutor_result) => tutor_result.tutor.id)
  }

  selectedResultsMatching = (predicate) => {
    const selected = this.state.selected_tutor_ids

    return _.filter(this.props.tutor_results, (tutor_result) => {
      return _.contains(selected, tutor_result.tutor.id) && predicate(tutor_result)
    })
  }

  selectedTutorIdsMatching = (predicate) => {
    return _.map(this.selectedResultsMatching(predicate), (tutor_result) => tutor_result.tutor.id)
  }

  onToggleSelect = (tutor_id) => {
    const selected_tutor_ids = _.contains(this.state.selected_tutor_ids, tutor_id)
      ? _.without(this.state.selected_tutor_ids, tutor_id)
      : this.state.selected_tutor_ids.concat([tutor_id])

    this.setState({ selected_tutor_ids })
  }

  onToggleSelectAll = () => {
    const selectable_ids = this.selectableTutorIds()
    const all_selected = selectable_ids.length > 0 && selectable_ids.length === this.state.selected_tutor_ids.length

    this.setState({ selected_tutor_ids: all_selected ? [] : selectable_ids })
  }

  onClearSelection = () => {
    this.setState({ selected_tutor_ids: [] })
  }

  onNotifySelected = () => {
    store.emit('tutoring_job:tutor_results:bulk_notify', {
      tutoring_job_id: this.props.tutoring_job.id,
      tutor_ids: this.selectedTutorIdsMatching(canNotify)
    })

    this.setState({ selected_tutor_ids: [] })
  }

  onRenotifySelected = () => {
    store.emit('tutoring_job:tutor_results:bulk_notify', {
      tutoring_job_id: this.props.tutoring_job.id,
      tutor_ids: this.selectedTutorIdsMatching(canRenotify),
      renotify: true
    })

    this.setState({ selected_tutor_ids: [] })
  }

  onRejectSelected = () => {
    store.emit('tutoring_job:tutor_results:bulk_reject', {
      tutoring_job_id: this.props.tutoring_job.id,
      tutor_ids: this.selectedTutorIdsMatching(canReject)
    })

    this.setState({ selected_tutor_ids: [] })
  }

  renderBulkActionPopover = ({ title, triggerLabel, triggerVariant, confirmVariant, results, onConfirm }) => {
    return (
      <Popover>
        <PopoverTrigger asChild>
          <Button variant={triggerVariant} size="sm">
            {triggerLabel} <ChevronUp className="size-3.5"/>
          </Button>
        </PopoverTrigger>
        <PopoverContent side="top" align="end" className="w-80">
          <div className="mb-2 text-sm font-semibold">{title}</div>
          <div className="max-h-56 space-y-0.5 overflow-y-auto">
            {_.map(results, (tutor_result) => {
              return (
                <div key={tutor_result.tutor.id} className="rounded-md px-2 py-1 text-sm">
                  <span className="truncate">{tutor_result.tutor.name}</span>
                </div>
              )
            })}
          </div>
          <div className="mt-3 flex justify-end">
            <Button variant={confirmVariant} size="sm" onClick={onConfirm}>
              {triggerLabel}
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    )
  }

  renderFilterDropdown = (label, options, value, fallback, onClick) => {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm">
            {label}: {_.findWhere(options, {value: value})?.name || fallback} <ChevronDown className="size-3.5"/>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {_.map(options, (option) => {
            return (
              <DropdownMenuItem
                key={option.value}
                className={cn(option.value === value && 'bg-muted')}
                onSelect={() => onClick(option.value)}
              >
                {option.name}
              </DropdownMenuItem>
            )
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    )
  }

  renderTutorResults = () => {
    if (this.props.ui.tutoring_job.loading_tutors) {
      return (
        <div className="py-6 text-center">
          <LoaderCircle className="mx-auto size-8 animate-spin text-muted-foreground"/>
        </div>
      )
    }
    else if (this.props.tutor_results.length === 0) {
      return (
        <div className="py-6 text-center text-sm text-muted-foreground">
          No results
        </div>
      )
    }

    return (
      <div className="flex flex-col gap-2">
        {_.map(this.props.tutor_results, (tutor_result) => {
          const tutor_proposals = _.select(this.props.tutor_proposals, function (tutor_proposal) {
            return tutor_proposal.tutor.id === tutor_result.tutor.id
          })

          return (
            <TutorResultsPartial
              customer_commitment={this.props.tutoring_job.commitment}
              learner_subjects={this.props.tutoring_job.learner_subjects}
              key={tutor_result.tutor.id}
              tutor_result={tutor_result}
              tutoring_job={this.props.tutoring_job}
              tutor_proposals={tutor_proposals}
              ui={this.props.ui}
              selectable={isSelectable(tutor_result)}
              selected={_.contains(this.state.selected_tutor_ids, tutor_result.tutor.id)}
              onToggleSelect={this.onToggleSelect}
            />
          )
        })}
      </div>
    )
  }

  render() {
    const selected_count = this.state.selected_tutor_ids.length
    const selectable_ids = this.selectableTutorIds()
    const all_selectable_selected = selectable_ids.length > 0 && selectable_ids.length === selected_count
    const notifiable_selected = this.selectedResultsMatching(canNotify)
    const renotifiable_selected = this.selectedResultsMatching(canRenotify)
    const rejectable_selected = this.selectedResultsMatching(canReject)

    const tutor_results_filter = this.props.ui.tutoring_job.tutor_results_filter
    const tutor_experience_level = this.props.ui.tutoring_job.tutor_experience_level
    const tutor_active_within = this.props.ui.tutoring_job.tutor_active_within
    const onboarded_after = this.props.ui.tutoring_job.onboarded_after

    const experienceLevelOptions = [
      { value: 'any', name: 'Any' },
      { value: '0', name: '0 Hours' },
      { value: '1_24', name: '1 - 24 Hours' },
      { value: '25_49', name: '25 - 49 Hours' },
      { value: '50_plus', name: '50 Hours +' }
    ]

    const activeWithinOptions = [
      { value: 'any', name: 'Any' },
      { value: '1_month', name: 'Last Month' },
      { value: '3_months', name: 'Last 3 Months' },
      { value: '6_months', name: 'Last 6 Months' },
      { value: '12_months', name: 'Last 12 Months' }
    ]

    const onboardedOptions = [
      { value: 'all', name: 'All Time' },
      { value: '3_months', name: 'Last 3 Months' },
      { value: '6_months', name: 'Last 6 Months' },
      { value: '1_year', name: 'Last Year' }
    ]

    const searchBioSuggestions = [
      { label: 'IEB', query: 'IEB' },
      { label: 'CAPS', query: 'CAPS' },
      { label: 'Cambridge', query: 'Cambridge' },
      { label: 'NSC', query: 'NSC' },
      { label: 'UCT', query: 'UCT | University of Cape Town' },
      { label: 'Stellenbosch University', query: 'Stellenbosch University' },
      { label: 'University of Pretoria', query: 'University of Pretoria' },
    ]

    const filterOptions = [
      { value: 'default', name: 'Contacted' },
      { value: 'searched', name: 'Potential Matches' }
    ]

    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="size-4"/>
            Tutors
            <Tabs value={tutor_results_filter} onValueChange={this.onFilter} className="ml-2">
              <TabsList className="group-data-horizontal/tabs:h-8">
                {_.map(filterOptions, (option) => (
                  <TabsTrigger key={option.value} value={option.value}>
                    {option.name}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          </CardTitle>
          <CardAction className="ml-auto self-center">
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={this.onAdd}><Plus className="size-3.5"/> Add Tutor</Button>
              <Button asChild variant="outline" size="sm">
                <a href={this.props.tutoring_job.links.tutor_browser} target="_blank">
                  Browse <ExternalLink className="size-3.5"/>
                </a>
              </Button>
            </div>
          </CardAction>
          {tutor_results_filter === 'searched' && (
            <div className="col-span-full mt-2 flex flex-wrap items-center justify-between gap-2">
              <div className="flex w-5/12 min-w-80 items-center gap-2">
                <InputGroup className="flex-1">
                  <InputGroupAddon><Search className="size-3.5"/></InputGroupAddon>
                  <InputGroupInput
                    type="text"
                    placeholder="Search bio &amp; subjects (press Enter)"
                    value={this.state.search_bio_input}
                    onChange={this.onSearchBioChange}
                    onKeyPress={this.onSearchBioKeyPress}
                  />
                  {this.state.search_bio_input && (
                    <InputGroupAddon>
                      <button type="button" className="cursor-pointer" onClick={this.onSearchBioClear} aria-label="Clear search">
                        <X className="size-3.5"/>
                      </button>
                    </InputGroupAddon>
                  )}
                </InputGroup>
                <PillSelect
                  icon={Tags}
                  options={_.map(searchBioSuggestions, (s) => ({ value: s.query, name: s.label }))}
                  placeholder="Suggestions"
                  onChange={this.onSearchBioSuggestion}
                />
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {this.renderFilterDropdown('Experience', experienceLevelOptions, tutor_experience_level, 'Any', this.onExperienceLevel)}
                {this.renderFilterDropdown('Activity', activeWithinOptions, tutor_active_within, 'Any', this.onActiveWithin)}
                {this.renderFilterDropdown('Onboarded', onboardedOptions, onboarded_after, 'All Time', this.onOnboardedAfterFilter)}
              </div>
            </div>
          )}
        </CardHeader>
        <CardContent className="min-h-[600px]">
          {this.state.adding ?
            <div className="mb-4 flex items-start gap-4 rounded-[10px] border border-border bg-muted/30 px-4 py-3">
              <p className="pt-1.5 text-sm font-semibold">Tutor</p>
              <div className="flex-1">
                <SearchTutors onChange={this.onSearchTutorsChange} onSelect={this.onAddTutor} value={this.state.tutor_search_term} />
              </div>
              <Button size="sm" onClick={this.onAddTutor}>Add</Button>
            </div>
            : null
          }
          <div className="mb-2 flex items-center px-4">
            <div className="flex w-8 shrink-0 items-center justify-center">
              {selectable_ids.length > 0 && (
                <Checkbox
                  checked={all_selectable_selected}
                  onCheckedChange={this.onToggleSelectAll}
                  aria-label="Select all tutors"
                />
              )}
            </div>
            <div className={cn(COLUMN_HEADING_CLASSES, 'w-[21%] text-left')}>
              Tutor
            </div>
            <div className={cn(COLUMN_HEADING_CLASSES, 'flex w-[35%]')}>
              <span className="w-1/4">State</span>
              <span className="w-3/4">Subjects</span>
            </div>
            <div className={cn(COLUMN_HEADING_CLASSES, 'w-[17%]')}>
              Availability
            </div>
            <div className={cn(COLUMN_HEADING_CLASSES, 'w-[8%]')}>
              Medium
            </div>
            <div className={cn(COLUMN_HEADING_CLASSES, 'w-[17%]')}>
              Risks
            </div>
          </div>
          {this.renderTutorResults()}
          {selected_count > 0 && (
            <div className="sticky bottom-4 z-10 mt-4 flex items-center gap-3 rounded-[10px] border border-primary/40 bg-card px-4 py-3 shadow-lg">
              <span className="text-sm font-semibold">
                {selected_count} {selected_count === 1 ? 'tutor' : 'tutors'} selected
              </span>
              <div className="ml-auto flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={this.onClearSelection}>
                  <X className="size-3.5"/> Clear
                </Button>
                {rejectable_selected.length > 0 && this.renderBulkActionPopover({
                  title: 'Reject these tutors?',
                  triggerLabel: `Reject ${rejectable_selected.length} ${rejectable_selected.length === 1 ? 'Tutor' : 'Tutors'}`,
                  triggerVariant: 'destructive',
                  confirmVariant: 'destructive',
                  results: rejectable_selected,
                  onConfirm: this.onRejectSelected
                })}
                {renotifiable_selected.length > 0 && this.renderBulkActionPopover({
                  title: 'Re-notify these tutors?',
                  triggerLabel: `Re-notify ${renotifiable_selected.length} ${renotifiable_selected.length === 1 ? 'Tutor' : 'Tutors'}`,
                  triggerVariant: 'outline',
                  confirmVariant: 'default',
                  results: renotifiable_selected,
                  onConfirm: this.onRenotifySelected
                })}
                {notifiable_selected.length > 0 && this.renderBulkActionPopover({
                  title: 'Notify these tutors?',
                  triggerLabel: `Notify ${notifiable_selected.length} ${notifiable_selected.length === 1 ? 'Tutor' : 'Tutors'}`,
                  triggerVariant: 'default',
                  confirmVariant: 'default',
                  results: notifiable_selected,
                  onConfirm: this.onNotifySelected
                })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    )
  }
}

export default TutorResults
